import { useState, useEffect } from "react";
import { Heart, Play, RotateCcw, X } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useStore } from "@/lib/store";
import { useXC } from "@/lib/hooks";
import { vodSources, fmtDuration } from "@/lib/xc";
import Player from "@/components/Player";

export default function MovieModal({ movie, onClose }) {
  const { active, isFav, toggleFav, pushRecent, resumeAt, pct, setProgress } = useStore();
  const [playing, setPlaying] = useState(false);
  const [startAt, setStartAt] = useState(0);
  const { data, loading } = useXC(active, "get_vod_info", { vod_id: movie?.id }, !!movie);
  useEffect(() => setPlaying(false), [movie?.id]);
  if (!movie) return null;
  const info = data?.info || {};
  const ext = data?.movie_data?.container_extension || movie.ext;
  const backdrop = (Array.isArray(info.backdrop_path) ? info.backdrop_path[0] : info.backdrop_path) || info.movie_image || movie.icon;
  const fav = isFav("movie", movie.id);

  const pkey = `movie:${movie.id}`;
  const resume = resumeAt(pkey);
  const play = (from) => {
    pushRecent({ ...movie, ext });
    setStartAt(from);
    setPlaying(true);
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0B0D14] border-[#212738] text-slate-100 max-w-5xl p-0 max-h-[92vh] overflow-y-auto xc-scroll [&>button]:hidden" data-testid="movie-modal">
        <DialogTitle className="sr-only">{movie.name}</DialogTitle>
        <div className="absolute top-3 right-3 z-20">
          <button onClick={onClose} className="p-2 rounded-full bg-black/60 hover:bg-black" data-testid="movie-modal-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="aspect-video relative bg-black">
          {playing ? (
            <Player sources={vodSources(active, "movie", movie.id, ext)} startAt={startAt} onProgress={(pos, dur) => setProgress({ [pkey]: { pos, dur } })} />
          ) : (
            <>
              {backdrop && <img src={backdrop} alt="" className="w-full h-full object-cover opacity-60" />}
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B0D14] via-[#0B0D14]/40 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-8">
                <h2 className="font-heading text-3xl lg:text-4xl font-extrabold tracking-tight" data-testid="movie-title">{info.name || movie.name}</h2>
                <div className="flex flex-wrap gap-2 mt-3 text-xs font-mono text-slate-300">
                  {[info.releasedate, info.duration, info.genre, ext?.toUpperCase()].filter(Boolean).map((t) => (
                    <span key={t} className="rounded-md border border-white/15 bg-white/5 px-2 py-0.5">{t}</span>
                  ))}
                </div>
                {pct(pkey) > 0 && (
                  <div className="h-1 w-64 rounded-full bg-white/15 mt-4 overflow-hidden" data-testid="movie-progress-bar">
                    <div className="h-full bg-[#E50914]" style={{ width: `${pct(pkey) * 100}%` }} />
                  </div>
                )}
                <div className="flex flex-wrap gap-3 mt-5">
                  {resume > 0 ? (
                    <>
                      <button onClick={() => play(resume)} className="xc-btn-primary" data-testid="movie-resume-button">
                        <Play className="w-4 h-4 fill-white" /> Resume from {fmtDuration(resume)}
                      </button>
                      <button onClick={() => play(0)} className="xc-btn-ghost" data-testid="movie-play-button">
                        <RotateCcw className="w-4 h-4" /> Start over
                      </button>
                    </>
                  ) : (
                    <button onClick={() => play(0)} className="xc-btn-primary" data-testid="movie-play-button">
                      <Play className="w-4 h-4 fill-white" /> Play
                    </button>
                  )}
                  <button onClick={() => toggleFav({ ...movie, ext })} className="xc-btn-ghost" data-testid="movie-fav-button">
                    <Heart className={`w-4 h-4 ${fav ? "fill-[#E50914] text-[#E50914]" : ""}`} /> {fav ? "In favourites" : "Add to favourites"}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
        <div className="px-8 pb-8 pt-4 text-sm text-slate-400 max-w-3xl">
          {loading ? "Loading details…" : info.plot || info.description || "No description available."}
          {info.cast && <div className="mt-3 text-xs text-slate-500">Cast: {info.cast}</div>}
        </div>
      </DialogContent>
    </Dialog>
  );
}
